import { forwardRef, useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import { styled } from 'goober';

import {
  exploreFormState,
  generatedShortDescriptionsState,
} from '@/features/explore/explore.state';
import { SmallText } from '@/ui/typography/widgets';

import { useCreateCompanyDescription } from './use-create-company-description-mutation';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKey } from '@/config/query-client';
import type { ExploreOrganizationSuggestion } from '@/api/v4/organization-suggestions.api';
import {
  isDescriptionNotFounded,
  sortByShortDescriptionAndRelevancy,
} from '@/features/explore/sort-by-short-description-and-result-number';
import { useAtom, useAtomValue } from 'jotai';
import { EllipsisTextTooltip } from '@/ui/table/infinite-table/ellipsis-text-tooltip';

type CompanyDescriptionLoaderProps = {
  companyName: string;
  companyWebsite?: string;
  shortDescription?: string;
  requestId: string;
  resultNumber: number;
  dataQueryKeyToSort?: string;
};

export const CompanyDescriptionLoader = ({
  companyName,
  companyWebsite,
  shortDescription,
  requestId,
  resultNumber,
  dataQueryKeyToSort,
}: CompanyDescriptionLoaderProps) => {
  const [generatedDescriptions, setGeneratedDescriptions] = useAtom(
    generatedShortDescriptionsState,
  );

  const exploreFormStateData = useAtomValue(exploreFormState);

  const { data, isPending: isLoading, mutate } = useCreateCompanyDescription();
  const queryClient = useQueryClient();
  const suggestionsQuery =
    queryClient.getQueryData<ExploreOrganizationSuggestion>([
      QueryKey.ExploreSuggestions,
    ]);

  const description =
    shortDescription ||
    generatedDescriptions[`${requestId}-${resultNumber}`] ||
    data;

  useEffect(() => {
    if (!description && companyName) {
      mutate(
        {
          requestId,
          resultNumber,
          companyName,
          website: companyWebsite,
          model: exploreFormStateData.model,
        },
        {
          onSuccess: async description => {
            setGeneratedDescriptions(mappings => {
              const newMappings = { ...mappings };
              newMappings[`${requestId}-${resultNumber}`] = description;
              return newMappings;
            });

            if (!isDescriptionNotFounded(description) || !suggestionsQuery)
              return;

            if (dataQueryKeyToSort === QueryKey.ExploreSuggestions) {
              const sortedResults = sortByShortDescriptionAndRelevancy(
                suggestionsQuery.results,
              );

              queryClient.setQueryData([QueryKey.ExploreSuggestions], {
                ...suggestionsQuery,
                results: sortedResults,
              });
            }
          },
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [description]);

  if (!description || isLoading) {
    return (
      <ShortDescription>
        <Skeleton width="100%" height="13px" />
        <Skeleton width="82" height="13px" />
      </ShortDescription>
    );
  }
  return (
    <EllipsisTextTooltip
      Component={
        <ShortDescription data-testid="enhance-suggestion-key-offering" />
      }
      text={description}
      maxWidth="520px"
    />
  );
};

const ShortDescription = styled(SmallText, forwardRef)`
  ${({ theme }) => theme.typography.enhance.tableSmallText}
  ${({ theme }) => theme.mixins.lineClamp(2)}
  width: 320px;
  white-space: normal;
`;
